import { Module, VuexModule, Mutation, getModule } from 'vuex-module-decorators';
import _ from 'lodash'
import Debug from 'debug';
import { ok, err } from '@xlearn/common'
import store from '@/store';
import { injectable } from 'inversify';
import { createUpserter } from '@/app/shared/utils'
import { mergeRightToLeft } from '../../shared/utils'
import { QuizSessionReducerPlugin, QuizSessionReducerPluginFactorySymbol  } from '../api'
import { QuizSessionQuery, RemoteStateQueryPhase, QuizSessionPhase } from './quiz-session-query'

export const QuizSessionStoreModuleSymbol = Symbol.for('QuizSessionStoreModule');

const upserter = createUpserter(defaultQuizSessionState);

const debug = Debug('quiz-session-store-module')

@injectable()
@Module({
  namespaced: true,
  name: 'quiz-session',
  store,
  dynamic: true
})
export class QuizSessionStoreModule extends VuexModule implements QuizSessionQuery {

  private remoteStateQueryPhase: RemoteStateQueryPhase = 'none';
  private localState: QuizSessionState[] = []
  private remoteState: QuizSessionState[] = []

  // @lazyInject(QuizSessionReducerPluginFactorySymbol)
  // private reducerFactory: (type: string) => QuizSessionReducerPlugin<unknown, unknown>
  
  @Mutation
  public addNewSession({ id, quizId, aspect, type, initialCommand, reducerFactory }: AddSessionPayload) {

    debug('addNewSession', id, quizId, type, initialCommand)
    
    const reducer = reducerFactory(type)
    const state = reducer.reduce({}, initialCommand)
    
    this.localState = upserter(this.localState, id, () => ({
      id: id,
      quizId: quizId,
      aspect: aspect,
      phase: 'active',
      type: type,
      content: state
    }))

    debug('state after addNewSession', this.localState)
  }

  @Mutation
  public updateSession({ id, command, reducerFactory }: UpdateSessionPayload) {

    debug('updateSession', id, command)

    if (!checkAndPrepareSession(id, this.localState, this.remoteState)) {
      return
    }

    this.localState = upserter(this.localState, id, (current) => {

      const reducer = reducerFactory(current.type)
      const state = reducer.reduce(current.content, command)
      return {
        ...current,
        content: state
      }
    })

    debug('state after updateSession', this.localState)
  }

  @Mutation
  public abortSession(id: string) {
    debug('abortSession', id)

    if (!checkAndPrepareSession(id, this.localState, this.remoteState)) {
      return
    }

    this.localState = upserter(this.localState, id, (current) => {
      return {
        ...current,
        phase: 'aborted'
      }
    })
  }

  @Mutation
  public finishSession(id: string) {
    debug('finishSession', id)

    if (!checkAndPrepareSession(id, this.localState, this.remoteState)) {
      return
    }

    this.localState = upserter(this.localState, id, (current) => {
      return {
        ...current,
        phase: 'finished'
      }
    })
  }

  @Mutation
  public startRemoteQuerying() {
    this.remoteStateQueryPhase = 'loading';
    this.remoteState = [];
  }

  @Mutation
  public finishRemoteQuerying(results: QuizSessionState[]) {
    this.remoteState = results;
    this.remoteStateQueryPhase = 'loaded';
  }

  // get findSession() {
  //   const self = this
  //   return function(type: string, sessionId: string) {
  //     const found = self.sessions.find(x => x.type === type && x.id === sessionId)
  //     if (!found) {
  //       return false
  //     }
      
      
  //   }
  // }

  get findSessionByQuizId() {
    const self = this
    return function(quizId: string, aspect: string) {
      const found = self.sessions.find(x => x.quizId === quizId && x.aspect === aspect && x.phase === 'active')
      if (!found) {
        return false
      }

      if (found.type === '') { throw new Error('Coding error: session found with empty type.')}

      return {
        id: found.id,
        type: found.type,
        phase: found.phase,
        state: found.content
      }
    }
  }

  get findSession() {
    const self = this
    return function(id: string) {
      const found = self.sessions.find(x => x.id === id)
      if (!found) { return false }
      if (found.type === '') { throw new Error('Coding error: session found with empty type.')}

      return {
        id: found.id,
        quizId: found.quizId,
        aspect: found.aspect,
        type: found.type,
        phase: found.phase,
        state: found.content
      }
    }
  }

  get sessions() {

    debug('merging quiz sessions updating remote with local', this.remoteState, this.localState)
    const merged = mergeRightToLeft(this.remoteState, this.localState, merge)
    debug('merged', merged)

    return merged

    function merge(_remote: QuizSessionState, local: QuizSessionState) {
      return local
    }
  }

  get remotePhase() {
    return this.remoteStateQueryPhase
  }
}

interface AddSessionPayload {
  id: string
  quizId: string
  aspect: string
  type: QuizType
  initialCommand: unknown
  reducerFactory: (name: string) => QuizSessionReducerPlugin
}

interface UpdateSessionPayload {
  id: string
  command: unknown
  reducerFactory: (name: string) => QuizSessionReducerPlugin
}

interface QuizSessionState {
  id: string
  quizId: string
  aspect: string
  phase: QuizSessionPhase
  type: QuizType
  content: unknown
}

type QuizType = '' | string // e.g. practice or exam

function defaultQuizSessionState(): QuizSessionState {
  return {
    id: '',
    quizId: '',
    aspect: '',
    phase: '',
    type: '',
    content: {}
  }
}

function checkAndPrepareSession(sessionId: string,
                                localState: QuizSessionState[], remoteState: QuizSessionState[]) {
  const exists = copyRemoteToLocalIfNotFound(sessionId, localState, remoteState)
    if (!exists) {
      throw new Error(`Coding error: Session does not exists with id ${sessionId}, skipping`)
      return false
    }

    return true
  }

function copyRemoteToLocalIfNotFound(sessionId: string,
                                     localState: QuizSessionState[], remoteState: QuizSessionState[]) {
  if (!localState.some(x => x.id === sessionId)) {
    const remote = remoteState.find(x => x.id === sessionId)
    if (remote) {
      debug('copying remote state to local', sessionId, remote)
      localState.push(_.cloneDeep(remote))
      return true
    }

    return false
  }

  return true
}

export default getModule(QuizSessionStoreModule);

